import React, { useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { UserX, UserCheck } from 'react-feather';
import { IReject } from './types';
import {
    Card,
    CardContent,
    CardHeader,
    Divider,
    makeStyles,
    Box,
    Button,
    Theme,
    ButtonProps,
} from '@material-ui/core';
import useLead from 'src/hooks/useLead';
import { RejectModal } from './RejectModal';
import {
    LEAD,
    LEAD_FOLLOWING,
    VISIT,
    VISIT_FOLLOWING,
    STATUSES,
} from 'src/constants/mongo/status';
import useAuth from 'src/hooks/useAuth';
import { authorize } from 'src/utils/auth';
import {
    ADMIN_LEVEL_ACCESS,
    DEVELOPER_LEVEL_ACCESS,
    GROUP_LEVEL_ACCESS,
} from 'src/components/access';

const useStyles = makeStyles((theme: Theme) => ({
    root: {},
    button: {
        height: 60,
    },
    reject: {
        backgroundColor: theme.palette.error.main,
        '&:hover': {
            backgroundColor: theme.palette.error.dark,
        },
    },
}));

export const Reject = ({
    leadId,
    canReject,
    isRejected,
    hasVisits,
    ...props
}: IReject) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { rejectLeadV2, updateLead } = useLead();
    const [comment, setComment] = useState<string>('');
    const [currentSubstatus, setCurrentSubstatus] = useState<string | null>('');
    const [open, setOpen] = useState<boolean>(false);
    const { user } = useAuth();

    const handleSubmit = () => {
        const leadStatus = {
            status: LEAD,
            substatus: LEAD_FOLLOWING,
        };

        const visitStatus = {
            status: VISIT,
            substatus: VISIT_FOLLOWING,
        };

        const LEAD_DATA = hasVisits ? visitStatus : leadStatus;

        updateLead(LEAD_DATA, leadId);
    };

    const BUTTONS_PROPS: ButtonProps = {
        fullWidth: true,
        variant: 'contained',
        color: 'primary',
    };

    const handleOpen = () => setOpen(true);

    const handleReject = () => {
        if (!currentSubstatus) return;
        rejectLeadV2({
            lead: leadId,
            comment: comment,
            substatus: currentSubstatus,
            status: STATUSES.REJECTED.ID,
        });
        setOpen(false);
        setComment('');
        setCurrentSubstatus(null);
    };

    return (
        <Card className={classes.root} {...props}>
            <RejectModal
                open={open}
                setOpen={setOpen}
                comment={comment}
                setComment={setComment}
                handleReject={handleReject}
                currentSubstatus={currentSubstatus}
                setCurrentSubstatus={setCurrentSubstatus}
            />
            <CardHeader title={t('Titles.RejectLead')} />
            <Divider />
            <CardContent>
                <Box display="flex" flexDirection="column" gridGap={15}>
                    <Button
                        {...BUTTONS_PROPS}
                        className={clsx(classes.button, classes.reject)}
                        startIcon={<UserX />}
                        onClick={handleOpen}
                        disabled={canReject}
                    >
                        {t('Titles.RejectLead')}
                    </Button>
                    {authorize(
                        user?.accessLevel,
                        DEVELOPER_LEVEL_ACCESS,
                        GROUP_LEVEL_ACCESS,
                        ADMIN_LEVEL_ACCESS
                    ) && (
                        <Button
                            {...BUTTONS_PROPS}
                            className={classes.button}
                            startIcon={<UserCheck />}
                            onClick={handleSubmit}
                            disabled={!isRejected}
                        >
                            {t('Buttons.AcceptLead')}
                        </Button>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};
